import React from 'react'
import gql from 'graphql-tag'
import memoize from 'lodash.memoize'
import {
  allAggregationConfigs,
  vulnerabilityOrSinkGroupFragment,
  VulnerabilityOrSinkGroup
} from './shared'
import {
  createVulnerabilitiesGrid,
  createVulnerabilitiesGridContext,
  createVulnerabilitiesGridContextProvider
} from './grid'
import { AggregationConfigs, AggregationOptions } from '../aggregations'
import { OrderFieldConfig } from '../../legacy-design-system/order'
import {
  getServerDrilldownID,
  getServerVulnerabilityDrilldownRoute,
  getServerVulnerabilityDrilldownID,
  getServerVulnerabilitiesDrilldownRoute
} from '../route'
import {
  getSeverityColumn,
  getVulnerabilityColumn,
  getApplicationColumn,
  getStatusColumn,
  getDateDetectedColumn,
  getRowActionColumn,
  getProtectStatusColumn
} from './grid-columns'
import { createWrappedPagingNav } from '../vulnerability-dashboard'
import { RouteContext } from '../route-provider'
import { getBulkActions } from './application-grid'
import { Column } from '../../legacy-design-system/grid/index'

type OrderField =
  | 'NAME'
  | 'LAST_DETECTED'
  | 'APPLICATION'
  | 'STATUS'
  | 'FIRST_DETECTED'
  | 'SEVERITY'

const orderFieldConfig: OrderFieldConfig<OrderField> = {
  SEVERITY: {
    direction: 'DESC',
    message: { id: 'title_severityLevel' }
  },
  NAME: {
    direction: 'ASC',
    message: { id: 'title_name' }
  },
  APPLICATION: {
    direction: 'ASC',
    message: { id: 'title_application' }
  },
  LAST_DETECTED: {
    direction: 'DESC',
    message: { id: 'title_vulnerabilities_lastDetected_vulnerability' }
  },
  FIRST_DETECTED: {
    direction: 'DESC',
    message: { id: 'title_firstDetected_vulnerability' }
  },
  STATUS: {
    direction: 'ASC',
    message: { id: 'title_status' }
  }
}

type AggregationCategory =
  | 'applications'
  | 'tags'
  | 'types'
  | 'severities'
  | 'statuses'
  | 'substatus'
  | 'urls'
  | 'securityStandards'
  | 'applicationTags'
  | 'sinks'
  | 'bugtrackerStatuses'
  | 'protectStatuses'

const {
  applications,
  tags,
  statuses,
  substatus,
  securityStandards,
  severities,
  types,
  urls,
  applicationTags,
  sinks,
  bugtrackerStatuses,
  protectStatuses
} = allAggregationConfigs

const aggregationConfigs: AggregationConfigs<AggregationCategory> = {
  applications,
  tags,
  statuses,
  substatus,
  securityStandards,
  severities,
  types,
  urls,
  sinks,
  applicationTags,
  bugtrackerStatuses,
  protectStatuses
}

const initialAggregationSelections: AggregationOptions<AggregationCategory> = {
  applications: [],
  tags: [],
  statuses: [],
  substatus: [],
  securityStandards: [],
  severities: [],
  types: [],
  urls: [],
  sinks: [],
  applicationTags: [],
  bugtrackerStatuses: [],
  protectStatuses: []
}

export const getPageQuery = memoize(
  (id: string) => gql`
    query ServerVulnerabilityGrid(
      $offset: Int!
      $limit: Int!
      $organizationID: ID!
      $orderBy: VulnerabilityOrder
      $filterText: String
      $quickFilter: String
      $dateFilter: VulnerabilityDateFilter
      $applications: [String!]
      $tags: [String!]
      $statuses: [String!]
      $substatus: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $types: [String!]
      $urls: [String!]
      $sinks: [String!]
      $applicationTags: [String!]
      $licensedOnly: Boolean!
      $groupedBy: VulnerabilityGroupedByFilter
      $bugtrackerStatuses: [String!]
      $protectStatuses: [String!]
    ) {
      vulnerabilitiesAndSinkGroups(
        offset: $offset
        limit: $limit
        organizationID: $organizationID
        orderBy: $orderBy
        filters: {
          servers: ["${id}"]
          filterText: $filterText
          quickFilter: $quickFilter
          dateFilter: $dateFilter
          applications: $applications
          tags: $tags
          statuses: $statuses
          substatus: $substatus
          securityStandards: $securityStandards
          severities: $severities
          types: $types
          urls: $urls
          sinks: $sinks
          applicationTags: $applicationTags
          licensedOnly: $licensedOnly
          groupedBy: $groupedBy
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatuses: $protectStatuses
        }
      ) {
        items {
          ...VulnerabilityOrSinkGroup
        }
        total
        licensedVulnerabilityCount
      }
    }

    ${vulnerabilityOrSinkGroupFragment}
  `
)

const getQuickFiltersQuery = memoize(
  (id) => gql`
    query ServerVulnerabilitiesQuickFilters(
      $organizationID: ID!
      $filterText: String
      $dateFilter: VulnerabilityDateFilter
      $applications: [String!]
      $tags: [String!]
      $statuses: [String!]
      $substatus: [String!]
      $securityStandards: [String!]
      $severities: [String!]
      $types: [String!]
      $urls: [String!]
      $sinks: [String!]
      $applicationTags: [String!]
      $licensedOnly: Boolean!
      $bugtrackerStatuses: [String!]
      $protectStatuses: [String!]
    ) {
      vulnerabilityQuickFilters(
        organizationID: $organizationID
        filters: {
          servers: ["${id}"]
          filterText: $filterText
          dateFilter: $dateFilter
          applications: $applications
          tags: $tags
          statuses: $statuses
          substatus: $substatus
          securityStandards: $securityStandards
          severities: $severities
          types: $types
          urls: $urls
          sinks: $sinks
          applicationTags: $applicationTags
          licensedOnly: $licensedOnly
          bugtrackerStatuses: $bugtrackerStatuses
          protectStatuses: $protectStatuses
        }
      ) {
        id
        name
        count
      }
    }
  `
)

const getAggregationQuery = (id: string, name: string, queryFragment: string) =>
  gql`
  query ServerVulnerabilities${name}Aggregations(
    $organizationID: ID!
    $filterText: String
    $quickFilter: String
    $dateFilter: VulnerabilityDateFilter
    $applications: [String!]
    $tags: [String!]
    $statuses: [String!]
    $substatus: [String!]
    $securityStandards: [String!]
    $severities: [String!]
    $types: [String!]
    $urls: [String!]
    $applicationTags: [String!]
    $sinks: [String!]
    $licensedOnly: Boolean!
    $bugtrackerStatuses: [String!]
    $protectStatuses: [String!]
  ) {
    vulnerabilityAggregations(
      organizationID: $organizationID
      filters: {
        servers: ["${id}"]
        filterText: $filterText
        quickFilter: $quickFilter
        dateFilter: $dateFilter
        applications: $applications
        tags: $tags
        statuses: $statuses
        substatus: $substatus
        securityStandards: $securityStandards
        severities: $severities
        types: $types
        urls: $urls
        applicationTags: $applicationTags
        sinks: $sinks
        licensedOnly: $licensedOnly
        bugtrackerStatuses: $bugtrackerStatuses
        protectStatuses: $protectStatuses
      }
    ) {
      ${queryFragment}
    }
  }
`

export const getAggregationQueries = memoize((id) => [
  getAggregationQuery(
    id,
    'Application',
    `applications {
      count
      key {
        id
        name
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Tag',
    `tags {
      count
      key
    }`
  ),
  getAggregationQuery(
    id,
    'Status',
    `statuses {
      count
      key {
        id
        label
        subfiltersId
        subfilters {
          count
          keycode
          label
        }
      }
    }`
  ),
  getAggregationQuery(
    id,
    'BugtrackerStatus',
    `bugtrackerStatuses {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'SecurityStandard',
    `securityStandards {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Severity',
    `severities {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Url',
    `urls {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'Type',
    `types {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'ApplicationTags',
    `applicationTags {
      count
      key
    }`
  ),
  getAggregationQuery(
    id,
    'Sink',
    `sinks {
      count
      key {
        id
        label
      }
    }`
  ),
  getAggregationQuery(
    id,
    'protectStatuses',
    `protectStatuses {
      count
      key {
        id
        label
      }
    }`
  )
])

export const ServerVulnerabiltiesGridContext = createVulnerabilitiesGridContext<
  OrderField,
  AggregationCategory
>({
  initialAggregationSelections,
  initialOrder: 'SEVERITY',
  aggregationConfigs,
  orderFieldConfig
})

export const getGridPath = ({ organizationID, routeParameters }) => {
  const serverId = getServerDrilldownID(routeParameters)

  if (!serverId) {
    throw new Error('Unable to parse server ID from route')
  }

  return getServerVulnerabilitiesDrilldownRoute(organizationID, serverId)
}

export const getDrilldownPath = ({ organizationID, id, routeParameters }) => {
  const serverID = getServerDrilldownID(routeParameters)

  if (!serverID) {
    throw new Error('Unable to parse server ID from route')
  }
  return getServerVulnerabilityDrilldownRoute(organizationID, serverID, id)
}

export const ServerVulnerabiltiesPagingNav = createWrappedPagingNav({
  // @ts-ignore todo TS-3.8.3 - TS-8508
  GridContext: ServerVulnerabiltiesGridContext,
  getCurrentIdFromRoute: getServerVulnerabilityDrilldownID,
  getGridPath,
  getDrilldownPath
})

const VulnerabilitiesGridMain = createVulnerabilitiesGrid(
  ServerVulnerabiltiesGridContext
)

const getQueries = memoize((serverID) => ({
  pageQuery: getPageQuery(serverID),
  aggregationsQueries: getAggregationQueries(serverID),
  quickFiltersQuery: getQuickFiltersQuery(serverID)
}))

export const ServerVulnerabilitiesGridContextProvider =
  createVulnerabilitiesGridContextProvider(
    // @ts-ignore
    ServerVulnerabiltiesGridContext,
    aggregationConfigs,
    'servers-vulns',
    'SEVERITY',
    orderFieldConfig,
    (routeParameters) => {
      const serverID = getServerDrilldownID(routeParameters)

      return !!serverID ? getQueries(serverID) : undefined
    },
    (routeParameters) => {
      return (
        routeParameters.length >= 4 &&
        routeParameters[1] === 'servers' &&
        routeParameters[3] === 'vulns'
      )
    }
  )

export function ServerVulnerabilitiesGrid() {
  return (
    <RouteContext.Consumer>
      {({ parameters }) => {
        const serverID = getServerDrilldownID(parameters)

        if (!serverID) {
          throw new Error('Unable to parse server ID from route')
        }

        return (
          <VulnerabilitiesGridMain
            isDrilldown
            renderBulkActions={getBulkActions<AggregationCategory>({
              aggregation: { servers: [serverID] },
              isServerGrid: true
            })}
            columns={({
              organizationID,
              organizationRole,
              hasLicenses,
              dateRange,
              dateRangeOption,
              onSetCustomDateRange,
              onSetDateRangeOption,
              onClearDateRange,
              dateField,
              onSetDateField,
              features,
              refetchQueries,
              refresh,
              trackJob,
              restrictedEditRole,
              protectAssessEnv,
              isProtectColumnEnabled,
              isProtectEnabled
            }) =>
              getColumns({
                serverID,
                organizationID,
                organizationRole,
                hasLicenses,
                dateRange,
                dateRangeOption,
                onSetCustomDateRange,
                onSetDateRangeOption,
                onClearDateRange,
                dateField,
                onSetDateField,
                features,
                refetchQueries,
                refresh,
                trackJob,
                restrictedEditRole,
                protectAssessEnv,
                isProtectColumnEnabled,
                isProtectEnabled
              })
            }
            showLicensedOnlyCheckbox
          />
        )
      }}
    </RouteContext.Consumer>
  )
}

function getColumns({
  serverID,
  organizationID,
  organizationRole,
  hasLicenses,
  dateRange,
  dateRangeOption,
  onSetCustomDateRange,
  onSetDateRangeOption,
  onClearDateRange,
  dateField,
  onSetDateField,
  features,
  refetchQueries,
  refresh,
  trackJob,
  restrictedEditRole,
  protectAssessEnv,
  isProtectColumnEnabled,
  isProtectEnabled
}): Array<Column<VulnerabilityOrSinkGroup, AggregationCategory>> {
  return [
    getSeverityColumn<AggregationCategory>({
      aggregations: ['severities'],
      organizationID,
      refetchQueries,
      refresh
    }),
    getVulnerabilityColumn<AggregationCategory>({
      aggregations: [
        'tags',
        'bugtrackerStatuses',
        'types',
        'sinks',
        'urls',
        'securityStandards'
      ],
      organizationID,
      organizationRole,
      dateField,
      features,
      hasLicenses,
      refresh,
      refetchQueries,
      getDrilldownPath: (id) =>
        getServerVulnerabilityDrilldownRoute(organizationID, serverID, id)
    }),
    getProtectStatusColumn<AggregationCategory>({
      aggregations: ['protectStatuses' as AggregationCategory],
      protectAssessEnv,
      isProtectColumnEnabled,
      isProtectEnabled
    }),
    getApplicationColumn<AggregationCategory>({
      organizationID,
      organizationRole,
      hasLicenses,
      features,
      aggregations: ['applications', 'applicationTags']
    }),
    getDateDetectedColumn<AggregationCategory>({
      dateRange,
      dateRangeOption,
      onSetCustomDateRange,
      onSetDateRangeOption,
      onClearDateRange,
      dateField,
      onSetDateField
    }),
    getStatusColumn<AggregationCategory>({
      organizationID,
      refetchQueries,
      refresh,
      aggregations: ['statuses', 'substatus']
    }),
    getRowActionColumn({
      organizationID,
      organizationRole,
      refresh,
      refetchQueries,
      trackJob,
      restrictedEditRole
    })
  ]
}
